import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Divider,
  Stack,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  IconButton,
  Button,
} from '@chakra-ui/react';
import { BadgeCheck, BadgeInfo, BadgeX, Calendar, Clock, Download } from "lucide-react";
import PdfIcon from "assets/svg/pdf.png";
import WordIcon from "assets/svg/word.png";
import ImageIcon from "assets/svg/jpg.png";
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DocumentIcon = ({ type }) => {
  switch (type?.toLowerCase()) {
    case "pdf": return <img src={PdfIcon} alt="PDF Icon" width={24} height={24} />;
    case "docx": return <img src={WordIcon} alt="Word Icon" width={24} height={24} />;
    case "jpeg":
    case "jpg": return <img src={ImageIcon} alt="Image Icon" width={24} height={24} />;
    default: return null;
  }
};

const ViewPaymentProofDrawer = ({ proof, onOpenReview }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  if (!proof) return null;

  const handleDocumentClick = (doc) => {
    setCurrentDocument({
      ...doc,
      file_path: `${BASE_URL.replace(/\/$/, '')}/${doc.file_path.replace(/^\/+/, '')}`
    });
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentDocument(null);
  };

  const downloadDocument = (path, filename) => {
    const url = `${BASE_URL.replace(/\/$/, '')}/${path.replace(/^\/+/, '')}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'Verified':
        return <Flex alignItems="center"><Icon as={BadgeCheck} size={12} color="green.500" ml={1}/><Text color="green.500" fontWeight="xs" fontSize="15px" ml={1}>Verified</Text></Flex>;
      case 'Pending':
        return <Flex alignItems="center"><Icon as={BadgeInfo} size={12} color="yellow.500" ml={1}/><Text color="yellow.500" fontWeight="xs" fontSize="15px" ml={1}>Pending</Text></Flex>;
      case 'Rejected':
        return <Flex alignItems="center"><Icon as={BadgeX} size={12} color="red.500" ml={1}/><Text color="red.500" fontWeight="xs" fontSize="15px" ml={1}>Rejected</Text></Flex>;
      default:
        return <Text color="gray.500">{status || "N/A"}</Text>;
    }
  };

  return (
    <Flex direction="column" p={8} width="100%">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="2xl" fontWeight="bold">Reference: {proof.reference ?? "N/A"}</Text>
        {renderStatus(proof.status)}
      </Flex>

      <Flex mt={2} alignItems="center">
        <Icon as={Calendar} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500" mr={4}>
          Uploaded: {proof.uploaded_at ? (() => { const date = new Date(proof.uploaded_at); date.setHours(date.getHours() + 2); return date.toLocaleString(); })() : "N/A"}
        </Text>
        <Icon as={Clock} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500">
          Verified: {proof.verified_at ? (() => { const date = new Date(proof.verified_at); date.setHours(date.getHours() + 2); return date.toLocaleString(); })() : "Not Verified"}
        </Text>
      </Flex>

      <Divider my={6} />

      {/* Driver Info */}
      <Box mb={6}>
        <Stack spacing={4}>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}><Text fontWeight="500" color="gray.500" fontSize="sm">Driver: {proof.name}</Text></Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}><Text fontWeight="500" color="gray.500" fontSize="sm">Phone: {proof.phone_number ?? "N/A"}</Text></Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}><Text fontWeight="500" color="gray.500" fontSize="sm">Plan: {proof.plan_name ?? "N/A"}</Text></Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}><Text fontWeight="500" color="gray.500" fontSize="sm">Amount: P{proof.amount ?? "0.00"}</Text></Flex>
          <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}><Text fontWeight="500" color="gray.500" fontSize="sm">Reference: {proof.reference ?? "N/A"}</Text></Flex>
        </Stack>
      </Box>

      <Divider my={6} />

      {/* Documents */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>Documents</Text>
        {proof.documents && proof.documents.length > 0 ? (
          <Stack spacing={2}>
            {proof.documents
              .filter(doc => doc.document_type?.toLowerCase() === "payment proof")
              .map((doc, index) => (
                <Tooltip key={index} label="Click to open document" hasArrow placement="top" bg="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" color="white">
                  <Flex p={2} border="1px" borderColor="gray.200" borderRadius="md" alignItems="center" _hover={{ cursor: 'pointer', bg: 'gray.100' }} onClick={() => handleDocumentClick(doc)}>
                    <DocumentIcon type={doc.file_extension} />
                    <Box ml={2}>
                      <Text fontWeight="bold">{doc.document_type ?? "Unknown"}</Text>
                      <Text color="gray.500">
                        Uploaded: {doc.uploaded_at ? (() => { const date = new Date(doc.uploaded_at); date.setHours(date.getHours() + 2); return date.toLocaleString(); })() : "N/A"}
                      </Text>
                    </Box>
                  </Flex>
                </Tooltip>
              ))}
          </Stack>
        ) : (
          <NoDataFound message="No documents uploaded" width="100px" height="100px" fontSize="sm" fontWeight="bold" />
        )}

        {/* Review Button */}
        <Box mt={4}>
          <Button bg="#071D54" color="white"    borderRadius="8px" onClick={() => onOpenReview(proof)}>Review</Button>
        </Box>
      </Box>

      {/* Modal Viewer */}
      <Modal isOpen={isOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent maxWidth="500px" maxHeight="80vh" overflowY="auto">
          <ModalHeader fontSize={"16px"}>Document Viewer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentDocument ? (
              <>
                <DocumentViewer documents={[currentDocument]} />
                <IconButton
                  aria-label="Download Document"
                  icon={<Download />}
                  onClick={() => downloadDocument(currentDocument.file_path.replace(BASE_URL, ''), currentDocument.document_type)}
                  bgGradient="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)"
                  color="white"
                  position="sticky"
                  bottom="20px"
                  left="20px"
                  zIndex="10"
                  _hover={{ bgGradient: "linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" }}
                  boxShadow="md"
                  borderRadius="full"
                  size="lg"
                />
              </>
            ) : (
              <NoDataFound message="No document to display." />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default ViewPaymentProofDrawer;
